import { Head, Link } from "@inertiajs/react"
import {
  MapPin,
  Leaf,
  Factory,
  FlaskConical,
  Mountain,
  Calendar,
  Coffee as CoffeeIcon,
  ChevronRight,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import type {
  VisibilityToggles,
  AIContent,
  CustomContent,
  PublishedCoffeeRecipe,
} from "@/types"
import { ScoreRadarChart } from "@/components/charts/ScoreRadarChart"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface Sample {
  id: number
  name: string
  origin: string | null
  land: string | null
  producer: string | null
  variety: string | null
  process: string | null
  description: string | null
}

interface Scores {
  fragrance_score: number | null
  acidity_score: number | null
  body_score: number | null
  flavor_score: number | null
  aftertaste_score: number | null
  balance_score: number | null
  overall_score: number | null
  uniformity_score: number | null
  clean_cup_score: number | null
  sweetness_score: number | null
  aroma_rating: number | null
  aroma_sca_selections: Record<string, string[]> | null
  roast_level: string | null
  total_score: number | null
}

interface Organization {
  name: string
  slug: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  website_url: string | null
  instagram_handle: string | null
}

interface CoffeeData {
  name: string
  slug: string
  ai_content: AIContent | null
  custom_content: CustomContent | null
  visibility_toggles: VisibilityToggles
  recipes: PublishedCoffeeRecipe[]
}

interface Props {
  coffee: CoffeeData
  sample: Sample | null
  scores: Scores | null
  cupping_form: string | null
  organization: Organization | null
}

function gradeFromScore(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: "Outstanding", color: "text-emerald-600" }
  if (score >= 85) return { grade: "Excellent", color: "text-green-600" }
  if (score >= 80) return { grade: "Very Good", color: "text-blue-600" }
  if (score >= 75) return { grade: "Good", color: "text-yellow-600" }
  return { grade: "Below Specialty", color: "text-muted-foreground" }
}

const methodLabels: Record<string, string> = {
  espresso: "Espresso",
  french_press: "French Press",
  auto_drip: "Auto Drip",
  v60: "V60 / Conic Pour Over",
  kalita_wave: "Kalita Wave / Flat Pour Over",
  elf_dripper: "Elf Dripper",
  origami: "Origami",
  eggonaut: "Eggonaut",
  meteorite_dripper: "Meteorite Dripper",
}

export default function PublicCoffee({
  coffee,
  sample,
  scores,
  cupping_form,
  organization,
}: Props) {
  const { t } = useTranslation()
  const toggles = coffee.visibility_toggles || ({} as VisibilityToggles)
  const protocol = cupping_form?.includes("cva") ? "cva" : "sca"
  const primaryColor = organization?.primary_color || "#6F4E37"
  const ai = coffee.ai_content
  const custom = coffee.custom_content
  const showGrade = toggles.show_score_grade !== false

  return (
    <>
      <Head title={`${coffee.name} | ${organization?.name || "Catador Pro"}`} />

      <div className="bg-background text-foreground min-h-screen">
        {/* Header */}
        <header className="border-b px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-4xl items-center justify-between">
            <div className="flex items-center gap-3">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {organization?.name?.charAt(0) || "C"}
                </div>
              )}
              <span className="text-sm font-semibold">
                {organization?.name}
              </span>
            </div>
            {organization?.website_url && (
              <a
                href={organization.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                {organization.website_url.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          {/* Hero Section */}
          <section className="mb-8 text-center sm:mb-10">
            <h1 className="mb-2 text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
              {coffee.name}
            </h1>
            {custom?.tagline && (
              <p className="text-muted-foreground mb-3 text-base sm:text-lg">
                {custom.tagline}
              </p>
            )}
            {ai?.description && (
              <p className="text-muted-foreground mx-auto max-w-2xl text-sm sm:text-base">
                {ai.description}
              </p>
            )}

            {/* Score Badge */}
            {toggles.show_scores && scores?.total_score && (
              <div className="mt-5 inline-flex flex-col items-center sm:mt-6">
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full border-4 text-white shadow-sm sm:h-24 sm:w-24"
                  style={{
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                  }}
                >
                  <span className="text-xl font-bold sm:text-2xl">
                    {scores.total_score.toFixed(1)}
                  </span>
                </div>
                {showGrade && (
                  <p
                    className={`mt-2 text-sm font-medium ${gradeFromScore(scores.total_score).color}`}
                  >
                    {gradeFromScore(scores.total_score).grade}
                  </p>
                )}
              </div>
            )}
          </section>

          <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="space-y-4 sm:space-y-6 lg:col-span-2">
              {/* Tasting Notes */}
              {toggles.show_tasting_notes && ai?.consumer_notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("public_coffee.tasting_notes", "Tasting Notes")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
                      {ai.consumer_notes}
                    </p>
                    {ai.flavor_tags && ai.flavor_tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {ai.flavor_tags.map((tag, i) => (
                          <Badge
                            key={i}
                            variant="secondary"
                            className="text-white"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Story */}
              {custom?.story && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("coffees.story_label", "Story")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
                      {custom.story}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Sensory Profile Chart */}
              {toggles.show_scores && scores && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("coffees.section_sensory", "Sensory Profile")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-60 w-full sm:h-[280px]">
                      <ScoreRadarChart
                        protocol={protocol}
                        scores={{
                          fragrance_score: scores.fragrance_score || 0,
                          aroma_rating: scores.aroma_rating || undefined,
                          flavor_score: scores.flavor_score || 0,
                          aftertaste_score: scores.aftertaste_score || 0,
                          acidity_score: scores.acidity_score || 0,
                          sweetness_score: scores.sweetness_score || 0,
                          body_score: scores.body_score || 0,
                          overall_score: scores.overall_score || 0,
                          balance_score: scores.balance_score || undefined,
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Brew Recipes */}
              {toggles.show_recipes && coffee.recipes.length > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-baseline justify-between">
                      <CardTitle className="text-base">
                        {t(
                          "public_coffee.recommended_recipes",
                          "Recommended Recipes",
                        )}
                      </CardTitle>
                      <span className="text-muted-foreground text-xs">
                        {t("public_coffee.methods_count", {
                          count: coffee.recipes.length,
                          defaultValue:
                            coffee.recipes.length === 1
                              ? `${coffee.recipes.length} method`
                              : `${coffee.recipes.length} methods`,
                        })}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="-mx-6 overflow-x-auto px-6">
                      <ul className="flex snap-x snap-mandatory gap-3 pb-2 [scrollbar-width:thin]">
                        {coffee.recipes.map((recipe) => (
                          <li
                            key={recipe.id}
                            className="w-[72%] shrink-0 snap-start sm:w-[48%] md:w-[46%]"
                          >
                            <Link
                              href={`/c/${coffee.slug}/recipes/${recipe.id}`}
                              className="group bg-card hover:border-primary/60 flex h-full flex-col rounded-lg border p-3 transition hover:shadow-sm sm:p-4"
                            >
                              <div className="mb-3 flex items-center gap-2">
                                <div
                                  className="flex h-9 w-9 items-center justify-center rounded-full"
                                  style={{
                                    backgroundColor: `${primaryColor}1A`,
                                  }}
                                >
                                  <CoffeeIcon
                                    className="h-4 w-4"
                                    style={{ color: primaryColor }}
                                  />
                                </div>
                                <h3 className="flex-1 text-sm leading-tight font-semibold">
                                  {methodLabels[recipe.method] ||
                                    recipe.method.replace(/_/g, " ")}
                                </h3>
                                <ChevronRight className="text-muted-foreground group-hover:text-foreground h-4 w-4 transition group-hover:translate-x-0.5" />
                              </div>
                              <dl className="mt-auto grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                                {recipe.ratio && (
                                  <div>
                                    <dt className="text-muted-foreground">
                                      {t("coffees.recipe_ratio", "Ratio")}
                                    </dt>
                                    <dd className="font-medium">
                                      {recipe.ratio}
                                    </dd>
                                  </div>
                                )}
                                {recipe.temp && (
                                  <div>
                                    <dt className="text-muted-foreground">
                                      {t("coffees.recipe_temp", "Temp")}
                                    </dt>
                                    <dd className="font-medium">
                                      {recipe.temp}°C
                                    </dd>
                                  </div>
                                )}
                                {recipe.dose && (
                                  <div>
                                    <dt className="text-muted-foreground">
                                      {t("coffees.recipe_dose", "Dose")}
                                    </dt>
                                    <dd className="font-medium">
                                      {recipe.dose}g
                                    </dd>
                                  </div>
                                )}
                                {recipe.grind && (
                                  <div className="truncate">
                                    <dt className="text-muted-foreground">
                                      {t("coffees.recipe_grind", "Grind")}
                                    </dt>
                                    <dd className="truncate font-medium">
                                      {recipe.grind}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Origin Card */}
              {toggles.show_origin && sample && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("public_coffee.origin", "Origin")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-3">
                      {sample.origin && (
                        <div className="flex items-start gap-3">
                          <MapPin className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.country", "Country")}
                            </dt>
                            <dd className="font-medium">{sample.origin}</dd>
                          </div>
                        </div>
                      )}
                      {sample.land && (
                        <div className="flex items-start gap-3">
                          <Mountain className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.region", "Region")}
                            </dt>
                            <dd className="font-medium">{sample.land}</dd>
                          </div>
                        </div>
                      )}
                      {toggles.show_producer && sample.producer && (
                        <div className="flex items-start gap-3">
                          <Factory className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.producer", "Producer")}
                            </dt>
                            <dd className="font-medium">{sample.producer}</dd>
                          </div>
                        </div>
                      )}
                      {sample.variety && (
                        <div className="flex items-start gap-3">
                          <Leaf className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.variety", "Variety")}
                            </dt>
                            <dd className="font-medium">{sample.variety}</dd>
                          </div>
                        </div>
                      )}
                      {sample.process && (
                        <div className="flex items-start gap-3">
                          <FlaskConical className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.process", "Process")}
                            </dt>
                            <dd className="font-medium">{sample.process}</dd>
                          </div>
                        </div>
                      )}
                      {custom?.altitude && (
                        <div className="flex items-start gap-3">
                          <Mountain className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.altitude", "Altitude")}
                            </dt>
                            <dd className="font-medium">{custom.altitude}</dd>
                          </div>
                        </div>
                      )}
                      {custom?.harvest && (
                        <div className="flex items-start gap-3">
                          <Calendar className="text-muted-foreground mt-0.5 h-4 w-4" />
                          <div>
                            <dt className="text-muted-foreground text-xs">
                              {t("public_coffee.harvest", "Harvest")}
                            </dt>
                            <dd className="font-medium">{custom.harvest}</dd>
                          </div>
                        </div>
                      )}
                    </dl>
                  </CardContent>
                </Card>
              )}

              {/* Technical Notes */}
              {toggles.show_tasting_notes && ai?.technical_notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t(
                        "public_coffee.professional_notes",
                        "Professional Notes",
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {ai.technical_notes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t px-4 py-6 text-center sm:px-6">
          <p className="text-muted-foreground text-xs">
            Powered by{" "}
            <a
              href="https://catador.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground font-medium"
            >
              Catador Pro
            </a>
          </p>
        </footer>
      </div>
    </>
  )
}
